import $ from 'jquery';
import {
    getPage,
    isCheckout,
    loadPayPalSdk,
    getFieldValue,
    setFieldValue,
    getErrorMessage,
    convertFormToData,
    submitErrorMessage,
    getPayPalQueryParams
} from "@ppcp/utils";
import {BaseGateway} from './class-base-gateway';

class CreditCardGateway extends BaseGateway {

    #orderId;

    #paymentToken;

    #fields = {
        number: {
            isEmpty: true,
            isValid: false
        },
        cvv: {
            isEmpty: true,
            isValid: false
        },
        expiry: {
            isEmpty: true,
            isValid: false
        }
    };

    #breakpoints = {
        xs: [0, 600],
        sm: [600, 900],
        md: [900, 1200],
        lg: [1200, Infinity]
    }

    constructor(props) {
        super(props);
        const {
            cart
        } = props;
        this.cart = cart;
        this.cardFields = [];
        this.isFastlane = false;
        this.mappings = [
            ['nameField', 'NameField', this.getCardNameFieldContainer(), ''],
            ['numberField', 'NumberField', this.getNumberFieldContainer(), 'cardNumberField'],
            ['cvvField', 'CVVField', this.getCVVFieldContainer(), 'cardCvvField'],
            ['expiryField', 'ExpiryField', this.getExpiryFieldContainer(), 'cardExpiryField']
        ];
    }

    async initialize() {

        window.addEventListener('resize', this.onWindowResize.bind(this));

        if (isCheckout()) {
            $('form.checkout').on('checkout_place_order_ppcp_card', this.handleCheckoutSubmit.bind(this));
            $(document.body).on('payment_method_selected', this.onPaymentMethodSelected.bind(this));
        } else {
            $(document.body).on('click', '#place_order', this.onClickPlaceOrder.bind(this));
            $(document.body).on('click', 'input[name="payment_method"]', this.onPaymentMethodSelected.bind(this));
        }

        $(document.body).on('checkout_error', this.onCheckoutError.bind(this));

        this.cart.on('updatedCheckout', this.onUpdatedCheckout.bind(this));

        await this.loadPayPalSDK();
    }

    async loadPayPalSDK() {
        try {
            this.paypal = await loadPayPalSdk(getPayPalQueryParams());
            this.createCardElement();
            this.mountCardElementFields();
            this.addBreakpointClasses();
        } catch (error) {
            console.log('Error loading PayPal SDK. ', error);
            submitErrorMessage(error, this.getForm(), 'checkout');
        }
    }

    createCardElement() {
        if (this.needsNewCardElement()) {
            this.cardElement = this.paypal.CardFields(this.getCardFieldOptions());
        }
    }

    getCardFieldOptions() {
        const source = this.paypal.FUNDING.CARD;
        return {
            fundingSource: source,
            inputEvents: this.getInputEvents(),
            style: this.settings.styles,
            onApprove: this.getOnApprove(source),
            onCancel: this.getOnCancel(source),
            onError: this.getOnError(source),
            onDestroy: this.getOnDestroy(source),
            createOrder: (data, actions) => {
                return this.createOrder(data, actions);
            },
            ...(this.settings.needsSetupToken && {
                createOrder: null,
                createVaultSetupToken: this.createVaultSetupToken.bind(this)
            })
        }
    }

    needsNewCardElement() {
        let bool = false;
        if (!this.cardElement) {
            bool = true;
        } else {
            bool = this.#getDOMFields(true).some($el => $el.find('iframe').length === 0);
        }
        return bool;
    }

    mountCardElementFields() {
        if (this.cardElement && this.cardElement.isEligible()) {
            for (const [key, fieldName, el] of this.mappings) {
                const $el = $(el);
                if ($el.length) {
                    if (!$el.find('iframe').length) {
                        this[key] = this.cardElement[fieldName](this.settings.fields[key]);
                        const id = '#' + $el.attr('id');
                        this[key].render(id);
                    }
                }
            }
        }
    }

    /**
     *
     */
    onUpdatedCheckout() {
        this.loadPayPalSDK();
    }

    onWindowResize(e) {
        if (this.windowResizeTimeout) {
            clearTimeout(this.windowResizeTimeout);
        }
        this.windowResizeTimeout = setTimeout(() => this.addBreakpointClasses(), 100);
    }

    handleCheckoutSubmit(e, wcCheckoutForm = null) {
        if (isCheckout()) {
            if (this.fastlaneToken) {
                return true;
            }
            if (this.isFastlane && this.#paymentToken && this.settings.needsSetupToken) {
                if (wcCheckoutForm.blockOnSubmit) {
                    wcCheckoutForm.blockOnSubmit(this.getForm());
                }
                this.createPaymentToken(this.#paymentToken, {fastlane: true}).then(response => {
                    setFieldValue('ppcp_card_payment_token', response.id, null);
                    this.fastlaneToken = response.id;
                    this.getForm().submit();
                }).catch((error) => {
                    this.submitError(error);
                    wcCheckoutForm?.$checkout_form?.removeClass('processing')?.unblock();
                });
                return false;
            }
        }
        if (this.#orderId || this.#paymentToken || this.isSavedPaymentMethodSelected()) {
            return true;
        }
        try {
            this.validateCheckoutForm();
            this.createPaymentMethod();
        } catch (error) {
            submitErrorMessage(error, this.getForm(), 'checkout');
            this.enablePlaceOrderButton(e);
            return false;
        }
        return false;
    }

    onClickPlaceOrder(e) {
        if (this.isPaymentGatewaySelected()) {
            if (!this.isSavedPaymentMethodSelected()) {
                e.preventDefault();
                this.createPaymentMethod(e);
            }
        }
    }

    onCheckoutError(e, message) {
        if (this.isPaymentGatewaySelected()) {
            this.enablePlaceOrderButton();
            if (!this.isFastlane) {
                this.#orderId = null;
                this.#paymentToken = null;
                this.fastlaneToken = null;
            }
        }
    }

    createPaymentMethod(e) {
        if (this.cardElement) {
            this.disablePlaceOrderButton(e);
            this.cardElement.submit(this.getCreatePaymentMethodProps()).catch(error => {
                this.enablePlaceOrderButton(e);
                if (error?.message && ['INVALID_NUMBER', 'INVALID_EXPIRY', 'INVALID_CVV'].includes(error.message)) {
                    return this.showCardFieldErrors(error.message);
                }
                return this.submitError(error);
            });
        }
    }

    createOrder(data, actions) {
        if (isCheckout()) {
            const formData = {...convertFormToData(this.getForm()), context: 'checkout', payment_method: this.id};
            return this.cart.createOrder(formData);
        } else {
            return this.cart.doOrderPay(
                this.id,
                {[`${this.id}_save_payment`]: this.isSavePaymentMethodChecked()}
            );
        }
    }

    onApprove(data) {
        if (data.orderID) {
            this.setOrderId(data.orderID);
        }
        if (data.vaultSetupToken) {
            this.createPaymentToken(data.vaultSetupToken).then((response) => {
                this.setPaymentToken(response.id);
                setFieldValue(`${this.id}_payment_token`, response.id, null);
                this.getForm().submit();
            }).catch(error => {
                return this.submitError(error?.message);
            }).finally(() => {
                this.enablePlaceOrderButton();
            })
            setFieldValue(`${this.id}_payment_token`, data.vaultSetupToken, null);
        } else {
            this.getForm().submit();
        }
    }

    enableButtons() {
        this.enablePlaceOrderButton();
    }

    submitError(error) {
        if (error?.code === 'validation_errors') {
            return submitErrorMessage(error.data.messages, this.getForm(), 'checkout');
        }
        return submitErrorMessage(error, this.getForm(), 'checkout');
    }

    setOrderId(value) {
        this.#orderId = value;
        setFieldValue(this.order_field_key, value, null);
    }

    getForm() {
        if (isCheckout()) {
            return $('form.checkout');
        } else {
            return $(this.getNumberFieldContainer()).closest('form');
        }
    }

    getCardNameFieldContainer() {
        return '#ppcp-card-name';
    }

    getNumberFieldContainer() {
        return '#ppcp-card-number';
    }

    getCVVFieldContainer() {
        return '#ppcp-card-cvv';
    }

    getExpiryFieldContainer() {
        return '#ppcp-card-exp';
    }

    disablePlaceOrderButton(e = null) {
        if (e) {
            $(e.currentTarget).prop('disabled', true);
        } else {
            $('form.checkout button[type="submit"]').prop('disabled', true);
        }
    }

    enablePlaceOrderButton(e = null) {
        if (e) {
            $(e.currentTarget).prop('disabled', false);
        } else {
            $('form.checkout button[type="submit"]').prop('disabled', false);
        }
    }

    getInputEvents() {
        return {
            onChange: this.onCardFieldChange.bind(this),
            onFocus: this.onCardFieldFocus.bind(this),
            onBlur: this.onCardFieldBlur.bind(this)
        }
    }

    onCardFieldChange(data) {
        const {
            fields,
            errors,
            emittedBy
        } = data;
        const [domField, field] = this.#getDOMFieldByName(emittedBy, fields);
        this.#fields[emittedBy] = field;
        const {
            isEmpty = false,
            isValid = false
        } = field;
        this.hideCardFieldError(domField.attr('id'));
        if (isValid) {
            domField.removeClass('card-field--invalid');
            domField.addClass('card-field--valid');
        } else {
            domField.removeClass('card-field--valid');
        }
    }

    onCardFieldFocus(data) {
        const {
            fields,
            errors,
            emittedBy
        } = data;

        const cardField = this.#getCardFieldByName(emittedBy);
        const [domField, field] = this.#getDOMFieldByName(emittedBy, fields);
        this.mappings.forEach(entry => {
            if (this[entry[0]]) {
                this[entry[0]].removeClass('focused')
            }
        });
        this.#getDOMFields().forEach(field => {
            field.removeClass('card-field--focused')
        });
        cardField.addClass('focused');
        domField.addClass('card-field--focused')
    }

    onCardFieldBlur(data) {
        const {
            fields,
            errors,
            emittedBy
        } = data;

        const [domField, field] = this.#getDOMFieldByName(emittedBy, fields);
        const cardField = this.#getCardFieldByName(emittedBy);
        cardField.removeClass('focused');
        domField.removeClass('card-field--focused');
        const {
            isValid,
            isEmpty
        } = field;
        if (!isEmpty) {
            if (!isValid) {
                domField.removeClass('card-field--valid');
                domField.addClass('card-field--invalid');
                this.showCardFieldError(this.#getCardFieldErrorCodeByName(emittedBy));
            }
        } else {
            domField.removeClass('card-field--invalid');
            cardField.removeClass('invalid');
            this.hideCardFieldError(domField.attr('id'));
        }
    }

    #getDOMFields(excludeName = false) {
        const result = [
            excludeName ? false : $(this.getCardNameFieldContainer()),
            $(this.getNumberFieldContainer()),
            $(this.getExpiryFieldContainer()),
            $(this.getCVVFieldContainer())
        ].filter(Boolean);
        return result;
    }

    #getCardFieldByName(name) {
        const entry = this.mappings.find(entry => {
            const [key] = entry;
            if (key.slice(0, name.length) === name) {
                return true;
            }
            return false;
        });
        return this[entry[0]];
    }

    #getCardFieldErrorCodeByName(name) {
        let code;
        switch (name) {
            case 'number':
                code = 'INVALID_NUMBER';
                break;
            case 'expiry':
                code = 'INVALID_EXPIRY';
                break;
            case 'cvv':
                code = 'INVALID_CVV';
                break;
        }
        return code;
    }

    #getDOMFieldByName(name, fields = {}) {
        let cardField, field;
        switch (name) {
            case 'name':
                field = fields.cardNameField;
                cardField = $(this.getCardNameFieldContainer());
                break;
            case 'number':
                field = fields.cardNumberField;
                cardField = $(this.getNumberFieldContainer());
                break;
            case 'expiry':
                field = fields.cardExpiryField;
                cardField = $(this.getExpiryFieldContainer());
                break;
            case 'cvv':
                field = fields.cardCvvField;
                cardField = $(this.getCVVFieldContainer());
                break;
        }
        return [cardField, field];
    }

    /**
     * Checks the container's width and assigns a class based on established breakpoints
     */
    addBreakpointClasses() {
        const $container = $(`li.payment_method_${this.id}`);
        if ($container.length) {
            const width = $container.outerWidth(true);
            let className = '';
            Object.keys(this.#breakpoints).forEach(key => {
                $container.removeClass(`card-field-container--${key}`);
            });
            const key = Object.keys(this.#breakpoints).find((key) => {
                const [r1, r2] = this.#breakpoints[key];
                if (r1 < width && r2 > width) {
                    return key;
                }
            });
            switch (key) {
                case 'xs':
                    className = 'card-field-container--xs'
                    break;
                case 'sm':
                    className = 'card-field-container--sm'
                    break;
                case 'md':
                    className = 'card-field-container--md'
                    break;
                case 'lg':
                    className = 'card-field-container--lg'
                    break;
            }
            $container.addClass(className);
        }
    }

    showCardFieldError(code) {
        if (this.settings.i18n.error_codes[code]) {
            const text = this.settings.i18n.error_codes[code];
            let $el;
            switch (code) {
                case 'INVALID_NUMBER':
                    $el = $(`[data-parent='${this.getNumberFieldContainer().slice(1)}']`);
                    break;
                case 'INVALID_EXPIRY':
                    $el = $(`[data-parent='${this.getExpiryFieldContainer().slice(1)}']`);
                    break;
                case 'INVALID_CVV':
                    $el = $(`[data-parent='${this.getCVVFieldContainer().slice(1)}']`);
                    break;
            }
            if ($el) {
                $el.html(`<span class="error-content">${text}</span>`);
                $el.animate({'max-height': '100px', opacity: '1'}, '350ms');
            }
        }
    }

    showCardFieldErrors() {
        Object.keys(this.#fields).forEach(key => {
            const {
                isValid,
                isEmpty
            } = this.#fields[key];
            if (!isValid || isEmpty) {
                const [domField] = this.#getDOMFieldByName(key);
                const code = this.#getCardFieldErrorCodeByName(key);
                const cardField = this.#getCardFieldByName(key);
                this.showCardFieldError(code);
                domField.addClass('card-field--invalid');
                cardField.addClass('invalid');
            }
        });
    }

    hideCardFieldError(id) {
        const $el = $(`[data-parent='${id}']`);
        $el.animate({'max-height': '0', opacity: '0'}, 350);
    }

    isPaymentGatewaySelected() {
        return $('[name="payment_method"]:checked')?.val() === this.id;
    }

    isSavedPaymentMethodSelected() {
        const selector = `[name="wc-${this.id}-payment-token"]:checked`;
        return this.isPaymentGatewaySelected() &&
            $(selector).length > 0 && $(selector).val() !== 'new';
    }

    getCreatePaymentMethodProps() {
        if (isCheckout() && !this.settings.needsSetupToken) {
            return {
                billingAddress: {
                    addressLine1: getFieldValue('billing_address_1'),
                    addressLine2: getFieldValue('billing_address_2'),
                    adminArea1: getFieldValue('billing_state'),
                    adminArea2: getFieldValue('billing_city'),
                    postalCode: getFieldValue('billing_postcode'),
                    countryCode: getFieldValue('billing_country')
                }
            }
        }
        return {};
    }

    onPaymentMethodSelected() {
        if (this.isPaymentGatewaySelected()) {
            this.showPlaceOrderButton();
        }
    }

    showPlaceOrderButton() {
        $('#place_order').removeClass('wc-ppcp-hide-button');
    }

    setPaymentToken(value) {
        this.#paymentToken = value;
    }

    validateCheckoutForm() {
        if ($('[name="terms"]').length) {
            if (!$('[name="terms"]').is(':checked')) {
                throw new Error(getErrorMessage({code: 'terms'}));
            }
        }
    }

    setIsFastlane(value) {
        this.isFastlane = value;
    }

    isSavePaymentMethodChecked() {
        return $(`[name="${this.id}_save_payment"]`).is(':checked');
    }

}

export {
    CreditCardGateway
}